/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getFlightType } from "Models/User";
import AutoCompletePlaces from "components/AutoCompletePlaces";
import { FieldRow, FieldSet, SpanError } from "components/Containers";
import { Input } from "components/Input";
import Label from "components/Label";
import Stack from "components/Stack";
import Text from "components/Text";
import { MyPhoneInput, RSSelectField } from "components/index";
import usePropUpdate from "hooks/usePropUpdate";

import { UseFormReturn } from "react-hook-form";
import { FlightFormSchemaType } from "../schema";
import useAirportSearch, { getAirportLabel } from "../useAirportSearch";
import { useTranslation } from "react-i18next";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/FormV2/FormV2";
import { FlightFieldsToExtract } from "lib/ai/flightAI";
import { useEffect } from "react";

const FlightInfoSection = ({
  form,
  type,
  defaultCountryCode,
  disabled,
  extractedFlightInfo,
}: {
  form: UseFormReturn<FlightFormSchemaType>;
  type: "outbound" | "return";
  flight_type: "commercial" | "pr" | "";
  defaultCountryCode?: string;
  disabled: boolean;
  extractedFlightInfo: FlightFieldsToExtract | null;
}) => {
  const { t } = useTranslation();
  const flight_type = form.watch(`${type}_flight_type`);
  return (
    <>
      <FieldSet isFullWidth>
        <RSSelectField
          id="flight_type"
          name="flight_type"
          value={flight_type}
          required
          label={t("flightInfoSection.flightTypeLabel")}
          onChange={(val: "1" | "2") => {
            form.setValue(`${type}_flight_type`, val ? val : "0");
            form.trigger(`${type}_flight_type`);
          }}
          error={form.formState.errors[`${type}_flight_type`] ? true : false}
          options={[
            { label: t("flightInfoSection.privateJetLabel"), value: "1" },
            { label: t("flightInfoSection.commercialLabel"), value: "2" },
          ]}
          disabled={disabled}
        />
        <SpanError
          error={form.formState.errors[`${type}_flight_type`]?.message ?? ""}
        ></SpanError>
      </FieldSet>
      {getFlightType(flight_type) === "commercial" && (
        <CommercialTypeSection
          form={form}
          type={type}
          disabled={disabled}
          extractedFlightInfo={extractedFlightInfo}
        />
      )}
      {getFlightType(flight_type) === "pr" && (
        <PrivateJetTypeSection
          form={form}
          type={type}
          defaultCountryCode={defaultCountryCode}
          disabled={disabled}
        />
      )}
    </>
  );
};

const CommercialTypeSection = ({
  form,
  type,
  disabled,
  extractedFlightInfo,
}: {
  form: UseFormReturn<FlightFormSchemaType>;
  type: "outbound" | "return";
  disabled: boolean;
  extractedFlightInfo: FlightFieldsToExtract | null;
}) => {
  const { t } = useTranslation();

  const fields = {
    flight_number: form.watch(`${type}_flight_number`),
    flight_pnr: form.watch(`${type}_flight_pnr`),
    from: form.watch(`${type}_departure_airport_code`),
    from_name: form.watch(`${type}_departure_airport_name`),
    to: form.watch(`${type}_arrival_airport_code`),
    to_name: form.watch(`${type}_arrival_airport_name`),
    departure_date: form.watch(`${type}_departure_date`),
    arrival_date: form.watch(`${type}_arrival_date`),
    departure_time: form.watch(`${type}_departure_time`),
    arrival_time: form.watch(`${type}_arrival_time`),
    duration_hours: form.watch(`${type}_duration_hours`),
    duration_minutes: form.watch(`${type}_duration_minutes`),
    cabin: form.watch(`${type}_cabin`),
  };

  const [fromOptions] = usePropUpdate(fields.from, (from) =>
    from && from?.length > 0
      ? [{ label: `${fields.from_name} (${from})`, id: from, value: from }]
      : [],
  );
  const [toOptions] = usePropUpdate(fields.to, (to) =>
    to && to?.length > 0
      ? [{ label: `${fields.to_name} (${to})`, id: to, value: to }]
      : [],
  );

  const [FlightFromSearchable, flightFromProps] = useAirportSearch({
    options: fromOptions,
  });
  const [FlightToSearchable, flightToProps] = useAirportSearch({
    options: toOptions,
  });

  const { onSearch: onSearchFromAirport } = flightFromProps;
  const { onSearch: onSearchToAirport } = flightToProps;

  const pickByCode = (
    options: { label: string; value: string }[],
    code: string,
  ) =>
    options?.find(
      (opt) => opt?.value?.toUpperCase() === code?.toUpperCase(),
    ) ?? { label: code, value: code };

  useEffect(() => {
    const extractedFromCode = extractedFlightInfo?.[
      (type + "_departure_airport_code") as keyof FlightFieldsToExtract
    ] as string | undefined;

    const extractedToCode = extractedFlightInfo?.[
      (type + "_arrival_airport_code") as keyof FlightFieldsToExtract
    ] as string | undefined;

    if (extractedFromCode) {
      onSearchFromAirport(extractedFromCode).then((opts) =>
        handleAirportSelect(
          "departure",
          pickByCode(Array.isArray(opts) ? opts : [], extractedFromCode),
        ),
      );
    }
    if (extractedToCode) {
      onSearchToAirport(extractedToCode).then((opts) =>
        handleAirportSelect(
          "arrival",
          pickByCode(Array.isArray(opts) ? opts : [], extractedToCode),
        ),
      );
    }
  }, [extractedFlightInfo]);

  const handleAirportSelect = (
    destiny: "departure" | "arrival",
    option: { label: string; value: string },
  ) => {
    const codeField = (type +
      "_" +
      destiny +
      "_airport_code") as keyof FlightFormSchemaType;
    const nameField = (type +
      "_" +
      destiny +
      "_airport_name") as keyof FlightFormSchemaType;
    const label = getAirportLabel(option);
    form.setValue(codeField, option.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue(nameField, label, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.clearErrors(codeField);
    form.clearErrors(nameField);

    return option.value;
  };
  return (
    <>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_flight_number`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.flightNumberLabel")}
                </FormLabel>
                <Input
                  type="text"
                  placeholder={t("flightInfoSection.flightNumberPlaceholder")}
                  {...field}
                  maxLength={128}
                  error={
                    form.formState.errors[`${type}_flight_number`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_flight_pnr`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.flightPNRLabel")}</FormLabel>
                <Input
                  type="text"
                  placeholder={t("flightInfoSection.flightPNRPlaceholder")}
                  {...field}
                  maxLength={128}
                  error={
                    form.formState.errors[`${type}_flight_pnr`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_airport_code`}
            render={() => (
              <FormItem>
                <FlightFromSearchable
                  {...flightFromProps}
                  value={fields.from}
                  label={t("flightInfoSection.fromLabel")}
                  isMissing={
                    form.formState.errors[`${type}_departure_airport_code`]
                      ? true
                      : false
                  }
                  required
                  onChange={(option: any) =>
                    handleAirportSelect("departure", option)
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_arrival_airport_code`}
            render={() => (
              <FormItem>
                <FlightToSearchable
                  {...flightToProps}
                  value={fields.to}
                  label={t("flightInfoSection.toLabel")}
                  isMissing={
                    form.formState.errors[`${type}_arrival_airport_code`]
                      ? true
                      : false
                  }
                  required
                  onChange={(option: any) =>
                    handleAirportSelect("arrival", option)
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_date`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.departureDateLabel")}
                </FormLabel>
                <Input
                  type="date"
                  {...field}
                  error={
                    form.formState.errors[`${type}_departure_date`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_arrival_date`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.arrivalDateLabel")}
                </FormLabel>
                <Input
                  type="date"
                  {...field}
                  error={
                    form.formState.errors[`${type}_arrival_date`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_time`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.departureDateTimeLabel")}
                </FormLabel>
                <Input
                  type="time"
                  {...field}
                  error={
                    form.formState.errors[`${type}_departure_time`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_arrival_time`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.arrivalTimeLabel")}
                </FormLabel>
                <Input
                  type="time"
                  {...field}
                  error={
                    form.formState.errors[`${type}_arrival_time`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <Label>{t("flightInfoSection.durationLabel")}</Label>
          <div className="flex gap-x-s15 items-center [direction:ltr]">
            <Stack>
              <FormField
                disabled={disabled}
                name={`${type}_duration_hours`}
                render={({ field }) => (
                  <FormItem>
                    <Input
                      type="number"
                      placeholder={t("guestInfoPage.hoursPlaceholder")}
                      {...field}
                      error={
                        form.formState.errors[`${type}_duration_hours`]
                          ? true
                          : false
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Stack>
            <Text>:</Text>
            <Stack>
              <FormField
                disabled={disabled}
                name={`${type}_duration_minutes`}
                render={({ field }) => (
                  <FormItem>
                    <Input
                      type="number"
                      placeholder={t("flightInfoSection.minutesPlaceholder")}
                      {...field}
                      error={
                        form.formState.errors[`${type}_duration_minutes`]
                          ? true
                          : false
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Stack>
          </div>
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_cabin`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.cabinLabel")}</FormLabel>
                <RSSelectField
                  id="cabin"
                  {...field}
                  options={[
                    { label: t("flightInfoSection.selectOption"), value: "" },
                    {
                      label: t("flightInfoSection.businessLabel"),
                      value: "Business",
                    },
                    {
                      label: t("flightInfoSection.firstClassLabel"),
                      value: "First Class",
                    },
                    {
                      label: t("flightInfoSection.economyLabel"),
                      value: "Economy",
                    },
                    {
                      label: t("flightInfoSection.premiumEconomyLabel"),
                      value: "Premium economy",
                    },
                  ]}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
    </>
  );
};

const PrivateJetTypeSection = ({
  form,
  type,
  defaultCountryCode,
  disabled,
}: {
  form: UseFormReturn<FlightFormSchemaType>;
  type: "outbound" | "return";
  disabled: boolean;
  defaultCountryCode?: string;
}) => {
  const { t } = useTranslation();

  const fields = {
    order_number: form.watch(`${type}_order_number`),
    aircraft: form.watch(`${type}_aircraft`),
    tail_number: form.watch(`${type}_tail_number`),
    call_sign: form.watch(`${type}_call_sign`),
    operator: form.watch(`${type}_operator`),
    departure_location: form.watch(`${type}_departure_location`),
    arrival_location: form.watch(`${type}_arrival_location`),
    handling_agents_country_code: form.watch(
      `${type}_handling_agents_country_code`,
    ),
    handling_agents: form.watch(`${type}_handling_agents`),
    handling_agents_contact_number: form.watch(
      `${type}_handling_agents_contact_number`,
    ),
    departure_date: form.watch(`${type}_departure_date`),
    arrival_date: form.watch(`${type}_arrival_date`),
    departure_time: form.watch(`${type}_departure_time`),
    arrival_time: form.watch(`${type}_arrival_time`),
    duration_hours: form.watch(`${type}_duration_hours`),
    duration_minutes: form.watch(`${type}_duration_minutes`),
  };
  return (
    <>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_order_number`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.orderNumberLabel")}</FormLabel>
                <Input
                  type="text"
                  {...field}
                  error={
                    form.formState.errors[`${type}_order_number`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_aircraft`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.aircraftLabel")}</FormLabel>
                <Input
                  type="text"
                  {...field}
                  error={
                    form.formState.errors[`${type}_aircraft`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_tail_number`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.tailNumberLabel")}
                </FormLabel>
                <Input
                  type="text"
                  {...field}
                  error={
                    form.formState.errors[`${type}_tail_number`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_call_sign`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.callSignLabel")}</FormLabel>
                <Input
                  type="text"
                  {...field}
                  error={
                    form.formState.errors[`${type}_call_sign`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldSet isFullWidth>
        <FormField
          disabled={disabled}
          name={`${type}_operator`}
          render={({ field }) => (
            <FormItem>
              <FormLabel isRequired>
                {t("flightInfoSection.OperatorLabel")}
              </FormLabel>
              <Input
                type="text"
                {...field}
                error={form.formState.errors[`${type}_operator`] ? true : false}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </FieldSet>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_location`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.departureLocationLabel")}
                </FormLabel>
                <AutoCompletePlaces
                  {...field}
                  value={fields.departure_location!}
                  onSelect={(address: string) => {
                    form.setValue(`${type}_departure_location`, address);
                    form.trigger(`${type}_departure_location`);
                  }}
                  onChange={(address: string) => {
                    form.setValue(`${type}_departure_location`, address);
                    form.trigger(`${type}_departure_location`);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_date`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.departureDateLabel")}
                </FormLabel>
                <Input
                  type="date"
                  {...field}
                  error={
                    form.formState.errors[`${type}_departure_date`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_departure_time`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.departureTimeLabel")}
                </FormLabel>
                <Input
                  type="time"
                  {...field}
                  error={
                    form.formState.errors[`${type}_departure_time`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <Label>{t("flightInfoSection.flightTimeLabel")}</Label>
          <div className="flex gap-x-s15 items-start [direction:ltr]">
            <Stack>
              <FormField
                disabled={disabled}
                name={`${type}_duration_hours`}
                render={({ field }) => (
                  <FormItem>
                    <Input
                      {...field}
                      type="number"
                      placeholder={t("guestInfoPage.hoursPlaceholder")}
                      // onChange={(e) => {
                      //   const { value } = e.target;

                      //   const numValue: number = Number(
                      //     verifyOnlyNumbers(removeLeadingZeros(value))
                      //   );

                      //   form.setValue(field.name, numValue, {
                      //     shouldValidate: true,
                      //     shouldDirty: true,
                      //   });
                      // }}
                      error={
                        form.formState.errors[`${type}_duration_hours`]
                          ? true
                          : false
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Stack>
            <Text>:</Text>
            <Stack>
              <FormField
                disabled={disabled}
                name={`${type}_duration_minutes`}
                render={({ field }) => (
                  <FormItem>
                    <Input
                      type="number"
                      placeholder={t("flightInfoSection.minutesPlaceholder")}
                      {...field}
                      // onChange={(e) => {
                      //   const { value } = e.target;

                      //   const numValue: number = Number(
                      //     verifyOnlyNumbers(removeLeadingZeros(value))
                      //   );

                      //   form.setValue(field.name, numValue, {
                      //     shouldValidate: true,
                      //     shouldDirty: true,
                      //   });
                      // }}
                      error={
                        form.formState.errors[`${type}_duration_minutes`]
                          ? true
                          : false
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Stack>
          </div>
        </FieldSet>
      </FieldRow>
      <FieldSet isFullWidth>
        <FormField
          disabled={disabled}
          name={`${type}_arrival_location`}
          render={({ field }) => (
            <FormItem>
              <FormLabel isRequired>
                {t("flightInfoSection.arrivalLocationLabel")}
              </FormLabel>
              <AutoCompletePlaces
                {...field}
                value={fields.arrival_location!}
                onSelect={(address: string) => {
                  form.setValue(`${type}_arrival_location`, address);
                  form.trigger(`${type}_arrival_location`);
                }}
                onChange={(address: string) => {
                  form.setValue(`${type}_arrival_location`, address);
                  form.trigger(`${type}_arrival_location`);
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </FieldSet>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_arrival_date`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.arrivalDateLabel")}
                </FormLabel>
                <Input
                  type="date"
                  inputClassName="min-h-[48px]"
                  {...field}
                  error={
                    form.formState.errors[`${type}_arrival_date`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_arrival_time`}
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("flightInfoSection.arrivalTimeLabel")}
                </FormLabel>
                <Input
                  type="time"
                  {...field}
                  error={
                    form.formState.errors[`${type}_arrival_time`] ? true : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
      <FieldRow>
        <FieldSet isFullWidth>
          <FormField
            disabled={disabled}
            name={`${type}_handling_agents`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("flightInfoSection.handlingAgentsLabel")}
                </FormLabel>
                <Input
                  type="text"
                  {...field}
                  error={
                    form.formState.errors[`${type}_handling_agents`]
                      ? true
                      : false
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
        <FieldSet isFullWidth={false}>
          <FormField
            disabled={disabled}
            name={`${type}_handling_agents_contact_number`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("flightInfoSection.contactNoLabel")}</FormLabel>
                <MyPhoneInput
                  value={fields.handling_agents_contact_number}
                  countryCode={
                    fields.handling_agents_country_code ?? defaultCountryCode
                  }
                  isMissing={
                    form.formState.errors[
                      `${type}_handling_agents_contact_number`
                    ]
                      ? true
                      : false
                  }
                  isCodeMissing={
                    form.formState.errors[
                      `${type}_handling_agents_country_code`
                    ]
                      ? true
                      : false
                  }
                  onChange={(phoneNumber) => {
                    form.setValue(
                      `${type}_handling_agents_contact_number`,
                      phoneNumber,
                    );
                    form.trigger(`${type}_handling_agents_contact_number`);
                  }}
                  setCountryCode={(code) => {
                    form.setValue(`${type}_handling_agents_country_code`, code);
                    form.trigger(`${type}_handling_agents_country_code`);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldSet>
      </FieldRow>
    </>
  );
};

export default FlightInfoSection;
